<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 border border-blueGray-200 rounded-lg bg-white"
  >
    <div class="rounded-t mb-0 px-4 py-3 border-0">
      <div class="flex flex-wrap items-center justify-between">
        <div class="relative px-4">
          <h3
            class="text-lg text-blueGray-700"
          >
          <i class="pi mx-2 text-sm pi-credit-card"></i> {{ isPersonal ? 'My Transactions' : `${ category && snakeToTitleCase(category)} Transactions` }}
          </h3>
        </div>

        <div class="flex flex-wrap items-center mb-3" style="gap: 0.25rem;">
          <button
            v-for="preset in datePresets"
            :key="preset.label"
            type="button"
            class="text-xs px-3 py-2 rounded outline-none focus:outline-none mr-1"
            :class="activePreset === preset.label
              ? 'bg-blueGray-900 text-white'
              : 'bg-blueGray-200 text-blueGray-600 hover:bg-blueGray-300'"
            @click="applyDatePreset(preset)"
          >
            {{ preset.label }}
          </button>
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button"
            :disabled="loading"
            @click="$emit('create')"
            v-if="canCreate"
          >
          <i class="pi pi-plus text-xs" style="font-size: 10px;"></i> Create New
          </button>
          
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button" 
            :disabled="loading"
            @click="toggleModal('filter', true)"
          >
          <i class="pi pi-filter text-xs" style="font-size: 10px;"></i> Filter
          </button>
          
          <button
            class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150 max-w-full"
            type="button" 
            @click="() => {get_csv=true;fetchNewTransactionPage()}"
            :disabled="!has('transaction.view') || loading || loaders().fetchTransactions" 
          >
          <i class="pi pi-download text-xs" style="font-size: 10px;"></i> Download
          </button>
        </div>

      </div>
    </div>


    <div class="block w-full overflow-x-auto">
      <!-- Projects table -->
      <table class="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
              v-if="!isPersonal"
            >
            <i class="pi pi-user mr-1"></i>Name
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
            <i class="pi pi-tag mr-1"></i>Type
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
              <i class="pi pi-dollar mr-1"></i>Amount
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
              <i class="pi pi-wallet mr-1"></i>Balance <br/>
              <small>(Before / After)</small>
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
              <i class="pi pi-flag mr-1"></i>Status
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
              <i class="pi pi-percentage mr-1"></i>Charges
            </th>
            
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
            <i class="pi pi-calendar mr-1"></i>Date
            </th>
            <th
              class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"
            >
            <i class="pi pi-info-circle mr-1"></i>Details
            </th>
          </tr>
        </thead>
        <tbody v-if="has('transaction.view')">
          <template v-if="!loaders().fetchTransactions">
            <tr 
              v-for="(trn, i) in transactions().data" 
              :key="i"
            >
              <th
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left"
                v-if="!isPersonal"
              >
                <span class="font-light">
                  {{ trn.user?.name }} <br/>
                  <small>{{ trn.user?.email }}</small>
                </span>
              </th>

              <th
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left"
              >
                <span class="font-light">
                  {{ snakeToTitleCase(trn.category) }} 
                  <small v-if="['CREDIT', 'DEBIT'].includes(trn.type)"
                  :class="[
                    trn.type === 'CREDIT' ? 'text-emerald-600' : '',
                    trn.type === 'DEBIT' ? 'text-orange-500' : '',
                    trn.type === 'NONE' ? 'text-blueGray-600' : '',
                  ]"
                  >({{ {"CREDIT": "CR", "DEBIT": "DR"}[trn.type] }})</small>
                </span>
              </th>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
              >
                <i :class="`fas mr-1 ${trn.type === 'CREDIT' ? 'fa-arrow-up text-emerald-500' : 'fa-arrow-down text-red-500'}`"></i>
                {{ formatter(trn.currency).format(trn.gross_amount) }}
              </td>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
              >
                {{ formatter(trn.currency).format(JSON.parse(trn.meta || "{}").balance_before || 0) }} / 
                {{ formatter(trn.currency).format(JSON.parse(trn.meta || "{}").balance || 0) }}
              </td>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 font-light"
              >
                <span 
                  class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                  :class="{
                    'bg-blueGray-900 text-white ring-blueGray-900 ring-opacity-20': trn.status === 'SUCCESSFUL',
                    'bg-red-300 text-red-600 ring-red-500 ring-opacity-10': trn.status === 'FAILED'
                  }"
                >
                  {{ trn.status }}
                </span>
              </td>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
              >
                <div class="flex items-center">
                  <span class="mr-2">{{ trn.charge }}</span>
                </div>
              </td>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
              >
                <div class="flex items-center">
                  <span class="mr-2">{{ moment(trn.created_at).format("DD MMM YY hh:mm") }}</span>
                </div>
              </td>
              <td
                class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left flex items-center"
                @click="transaction = {
                  id: trn.id,
                  reference: trn.reference,
                  desc: trn.description, 
                  user_created_at: trn.user?.created_at,
                  ...(JSON.parse(trn.meta) || {})
                }; confirmReversal = false; toggleModal('transaction', true)"
              >
                <!-- <trans-dropdown :data="{}" /> -->
                <span class="text-blueGray-500 py-1 px-3"><i class="fas fa-ellipsis-v"></i></span>
              </td>
            </tr>
            <!-- 
            <tr>
            <th
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-left flex items-center"
            >
              <img
                :src="react"
                class="h-12 w-12 bg-white rounded-full border"
                alt="..."
              />
              <span
                class="ml-3 font-bold"
                :class="[
                  color === 'light' ? 'text-blueGray-600' : 'text-white',
                ]"
              >
                React Material Dashboard
              </span>
            </th>
            <td
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
            >
              $4,400 USD
            </td>
            <td
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
            >
              <i class="fas fa-circle text-teal-500 mr-2"></i> on schedule
            </td>
            <td
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
            >
              <div class="flex">
                <img
                  :src="team1"
                  alt="..."
                  class="w-10 h-10 rounded-full border-2 border-blueGray-50 shadow"
                />
                <img
                  :src="team2"
                  alt="..."
                  class="w-10 h-10 rounded-full border-2 border-blueGray-50 shadow -ml-4"
                />
                <img
                  :src="team3"
                  alt="..."
                  class="w-10 h-10 rounded-full border-2 border-blueGray-50 shadow -ml-4"
                />
                <img
                  :src="team4"
                  alt="..."
                  class="w-10 h-10 rounded-full border-2 border-blueGray-50 shadow -ml-4"
                />
              </div>
            </td>
            <td
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
            >
              <div class="flex items-center">
                <span class="mr-2">90%</span>
                <div class="relative w-full">
                  <div
                    class="overflow-hidden h-2 text-xs flex rounded bg-teal-200"
                  >
                    <div
                      style="width: 90%;"
                      class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-teal-500"
                    ></div>
                  </div>
                </div>
              </div>
            </td>
            <td
              class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right"
            >
              <table-dropdown />
            </td>
          </tr>
           -->
          </template>
          <template v-else>
            <tr v-for="n in 6" :key="n">
              <td v-for="col in 8" :key="col" class="border-t-0 px-6 align-middle border-l-0 border-r-0 p-4">
                <div class="h-3 bg-blueGray-200 rounded animate-pulse" :style="{ width: ['85%','60%','70%','50%','65%','55%','45%'][(col-1) % 7] }"></div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <permission-err v-if="!has('transaction.view')"></permission-err>
      <empty-list v-if="(!transactions().data || transactions().data.length === 0) && !loaders().fetchTransactions && has('transaction.view')"></empty-list>
    </div>
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <circular-pagination :current_page="transactions().current_page" :last_page="transactions().last_page" :size="pageSize" @paginate="pageChange" ></circular-pagination>
      </div>
    </div>
    <transition-group>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }">
        <div v-if="modals.transaction" style="width: 100%;" class="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none justify-center items-center flex w-screen h-screen top-0 left-0">
          <div class="relative w-auto my-6 mx-auto">
            <!--content-->
            <div class="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <!--header-->
              <div class="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                <h3 class="text-xl font-semibold">
                  Transaction
                </h3>
                <button 
                  class="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none" 
                  v-on:click="toggleModal('transaction', false)"
                >
                  <span class="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                    ×
                  </span>
                </button>
              </div>
              <!--body-->
              <div class="relative p-6 flex-auto">
                <recursive-table :data="transaction" :exclude="['id']" :copies="['reference']" />
                <div v-if="confirmReversal">
                  <hr/>
                  <div class="flex flex-wrap mt-4 items-center">
                    <div class="w-8/12 pl-4">
                      <div class="relative w-full flex">
                        <label
                          class="block text-blueGray-600 text-xs mb-2"
                          htmlFor="grid-password"
                        >
                          Enter Reference 
                        </label>
                        <input
                          type="text"
                          class="border-0 px-3 py-1 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-xs shadow focus:outline-none w-full ease-linear transition-all duration-150"
                          v-model="confirmReference"
                          placeholder="Transaction Reference"
                        />
                      </div>
                    </div>

                    <div class="w-4/12 px-4">
                      <button
                        class="bg-blueGray-900 text-white active:bg-blueGray-800 text-xs px-3 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 w-full"
                        type="button" 
                        @click="reverseTransaction" 
                        :disabled="loaders().reverse || !confirmReference"
                        v-if="has('wallet.debit.ability')"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <!--footer reverseTransaction-->
              <div class="flex items-center justify-end p-4 border-t border-solid border-blueGray-200 rounded-b">
                <button 
                  class="text-blueGray-900 background-transparent px-3 py-1 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  @click="() => confirmReversal = true"
                  :disabled="loaders().reverse"
                  v-if="has('wallet.debit.ability')"
                >
                  Reverse
                </button>

                <button 
                  class="text-red-500 background-transparent px-3 py-1 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button" 
                  v-on:click="toggleModal('transaction', false)"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
      <transition name="bounce" :duration="{ enter: 500, leave: 800 }">
        <div v-if="modals.transaction" style="width: 100%; opacity: 0.25;" class="opacity-25 fixed inset-0 z-40 h-screen top-0 left-0 bg-black"></div>
      </transition>
    </transition-group>
    
    <filter-modal 
      :selects="{
        category: category ? [category] : transactionList, 
        status: statusList || []
      }"
      :dates="{
        from: 'Start Date',
        to: 'End Date'
      }"
      :searchable="true"
      :sizable="true"
      :visible="modals.filter"
      @close="modals.filter=false" 
      title="transaction"
      @filter="filter"
      :loading="loading || loaders().fetchTransactions"
    ></filter-modal>

  </div>
</template>
<script>
// import TransDropdown from "@/components/Dropdowns/TransDropdown.vue";
import CircularPagination from "@/components/Paginations/CircularPagination.vue";
import { transactionList, formatter, statusList, snakeToTitleCase } from '../../utils'
import bootstrap from "@/assets/img/bootstrap.jpg";
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { createToaster } from "@meforma/vue-toaster";
import moment from "moment"
import RecursiveTable from '@/components/Tables/RecursiveTable.vue';
const toaster = createToaster({ /* options */ });
// import datepicker from 'js-datepicker'
import FilterModal from "@/components/Modals/FilterModal.vue";
import EmptyList from "@/components/Cards/EmptyList.vue";
import PermissionErr from "@/components/Cards/PermissionErr.vue";

export default {
  data() {
    return {
      bootstrap,
      pageSize: 10,
      params: "",
      loading: false,
      get_csv: false,
      pagination: {},
      transactionList,
      modals: {},
      transaction: {},
      confirmReversal: false,
      confirmReference: "",
      from: null,
      to: null,
      activePreset: null,
      datePresets: [
        { label: 'Today', days: 1 },
        { label: '7D', days: 7 },
        { label: '30D', days: 30 },
      ],
      statusList
    };
  },
  components: {
    // TransDropdown,
    RecursiveTable,
    CircularPagination,
    FilterModal,
    EmptyList,
    PermissionErr
  },
  props: {
    color: {
      default: "light",
      validator: function (value) {
        // The value must match one of these strings
        return ["light", "dark"].indexOf(value) !== -1;
      },
    },
    isPersonal: {
      default: false,
      type: Boolean,
    },
    category: {
      type: String,
      required: false
    },
    canCreate: {
      default: false,
      type: Boolean,
      required: false
    }
  },
  watch: {
    category: function() {
      this.loadInitialData()
    }
  },
  methods: {
    ...mapActions(["getTransactions", "getMyTransactions", "reverse"]),
    ...mapGetters(["transactions", "loaders"]),
    ...mapMutations(["updateTransactionQueryParams"]),
    loadInitialData() {
      const params = {}
      const {search} = this.$route.query;
      if ( search ) {
        params.search = search
      }
      if (this.category) {
        params.category = this.category
      }
      const query = this.isPersonal ? this.getMyTransactions : this.getTransactions;
      query(params)
    },
    pageChange(pagination) {
      this.pagination = pagination;
      this.fetchNewTransactionPage();
    },
    filter(params) {
      this.activePreset = null;
      this.params = params;
      this.fetchNewTransactionPage(params);
    },
    // Today/7D/30D shortcuts — reuses the exact same filter() path the modal's own
    // date pickers go through, so this stays consistent with manual date filtering
    // instead of being a parallel, easily-diverging mechanism.
    applyDatePreset(preset) {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(toDate.getDate() - (preset.days - 1));
      const toIso = d => d.toISOString().slice(0, 10);
      this.filter({ from: toIso(fromDate), to: toIso(toDate) });
      this.activePreset = preset.label;
    },
    fetchNewTransactionPage(p={}) {
      const params = {
        ...this.pagination,
        get_csv: this.get_csv,
        ...this.params,
        ...p
      }
      if (this.category) {
        params.category = this.category
      }
      if ( !params.get_csv ) {
        this.updateTransactionQueryParams(params)
        this.$emit('transactionChange', params)
      }
      const self = this
      this.loading = true

      const query = this.isPersonal ? this.getMyTransactions : this.getTransactions;

      query(params).then( response => {
        if ( self.get_csv ) {
          // create file link in browser's memory
          const href = window.URL.createObjectURL(response);
          // create "a" HTML element with href to file & click
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download','transactions.csv'); //or any other extension
          document.body.appendChild(link);
          link.click();
          // clean up "a" element & remove ObjectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);

          toaster.success("Report downloaded Successfully");
        }
      }).finally(() => {
        self.get_csv = false
        self.loading = false
      })
    },
    toggleModal(modalName, isvisible) {
      this.modals[modalName] = isvisible
    },
    async reverseTransaction() {
      const transaction = this.transaction;
      if (!this.confirmReversal || transaction.reference !== this.confirmReference) {
        toaster.error("Please confirm the reversal of this transaction")
        return
      }
      await this.reverse(transaction)
    }
  },
  mounted() {
    this.loadInitialData()
  },
  created() {
    this.moment = moment
    this.formatter = formatter
    this.snakeToTitleCase = snakeToTitleCase
  }
};
</script>
